import {
  type Dispatch,
  type FC,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ErrorLike } from '@apollo/client';
import { LogOutIcon } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type {
  Doc_Template_Type_Enum_Enum,
  GetCmaReportForPreviewQuery,
} from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import type { ListingBrochureType } from '@modules/reports/brochure-report/BrochureReportEditor/BrochureReportEditor';
import type { CMAReportType } from '@modules/reports/cma-reports/CMAReportEditor/CMAReportEditor';
import type {
  CMAPageId,
  ListingBrochurePageId,
} from '@modules/reports/shared/utils';
import { useAnalytics } from '@shared/analytics';
import { type Language, useLocale } from '@shared/hooks/locale';

export type ReportData =
  | CMAReportType
  | ListingBrochureType
  | NonNullable<GetCmaReportForPreviewQuery['cma_reports_by_pk']>;

export type StepPosition = 'main' | 'footer';

export type ReportComponentProps = {
  step: number;
  setStep: (step: number) => void;
  reportId: string;
};

export type PageId = CMAPageId | ListingBrochurePageId;

export type StepType<T extends string> = {
  title: string;
  Component?: FC<ReportComponentProps>;
  pageId?: T;
  icon: ReactNode;
  activeIcon?: ReactNode;
  active?: boolean | null;
  databaseToggleField?: string;
  position: 'main' | 'footer';
  onClick?: () => void;
  /**
   * Indicates if this step is navigable in mobile view.
   * Main steps are typically navigable, footer steps (like "Save and exit") are not.
   * If not provided, defaults to true for main steps and false for footer steps.
   */
  navigable?: boolean;
};

export type ReportEditorContextType<T extends string> = {
  currentStep: StepType<T>;
  data: ReportData;
  mobileTab: 'viewer' | 'editor';
  setMobileTab: (value: 'viewer' | 'editor') => void;
  setCurrentStepByIndex: (index: number) => void;
  documentId: string;
  templateId: string;
  viewerPageId: T;
  setViewerPageId: (pageId: T) => void;
  steps: StepType<T>[];
  mainSteps: StepType<T>[];
  footerSteps: StepType<T>[];
  /**
   * A filtered list of steps that can be navigated in mobile view.
   * By default, all main steps are navigable and footer steps are not.
   * This can be overridden with the 'navigable' property on individual steps.
   * This is used by MobileStepperHeader to determine which steps can be navigated to.
   */
  navigableSteps: StepType<T>[];
  currentStepData: StepType<T>;
  currentStepIndex: number;
  setError: (error: ErrorLike) => void;
  isSidebarExpanded: boolean;
  setSidebarExpanded: (isExpanded: boolean) => void;
  isContentPanelExpanded: boolean;
  setContentPanelExpanded: (isExpanded: boolean) => void;
  documentType: Doc_Template_Type_Enum_Enum;
  language: Language;
  handleScrollToPage: (pageId: T, index: number) => void;
  scrollToPageRef: MutableRefObject<(pageId: PageId) => void>;
  iFrameRef: MutableRefObject<HTMLIFrameElement | null>;
  /**
   * Reference to the currently active iframe (may be the original or a buffer iframe
   * used for double-buffering). Use this ref for operations like printing.
   */
  activeIframeRef: MutableRefObject<HTMLIFrameElement | null>;
  /**
   * Sets the active iframe ref from the responsive iframe renderer
   */
  setActiveIframeRef: (ref: MutableRefObject<HTMLIFrameElement | null>) => void;
  /**
   * List of fields to include in the visibility mutation
   * These are the fields that can be toggled in the report
   */
  includeFields?: string[];
  renderFilename: string | undefined;
  setRenderFilename: Dispatch<SetStateAction<string | undefined>>;
  /**
   * Registers a callback to toggle step visibility by index.
   * Used by the disabled step overlay CTA to enable the section.
   */
  toggleStepVisibilityRef: MutableRefObject<((index: number) => void) | null>;
  /**
   * When true, the report is in preview-only mode (no editing actions available)
   */
  previewOnly: boolean;
  readOnly: boolean;
  previewVersion?: string;
  descriptionLanguage: Language | null;
  setDescriptionLanguage: (language: Language | null) => void;
  /**
   * The latest rendered HTML markup from the template renderer.
   * Stored here so step components can parse it with DOMParser
   * instead of querying the iframe DOM (which may not be accessible on mobile).
   */
  markup: string | null;
  setMarkup: Dispatch<SetStateAction<string | null>>;
};

// Use generic parameter for the context
const ReportEditorContext = createContext<ReportEditorContextType<any> | null>(
  null,
);

type ProviderProps<T extends PageId> = {
  children: ReactNode;
  data: ReportData;
  steps: StepType<T>[];
  documentId: string;
  templateId: string;
  exitPath: string;
  documentType: Doc_Template_Type_Enum_Enum;
  language?: Language;
  includeFields: string[];
  previewOnly?: boolean;
  readOnly?: boolean;
  previewVersion?: string;
};

const ReportEditorProvider = <T extends PageId>({
  children,
  data,
  steps,
  documentId,
  templateId,
  exitPath,
  documentType,
  language,
  includeFields = [],
  previewOnly = false,
  readOnly = false,
  previewVersion,
}: ProviderProps<T>) => {
  const { t, language: defaultLanguage } = useLocale();
  const navigate = useNavigate();
  const { pageId } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const [isSidebarExpanded, setSidebarExpanded] = useState(true);
  const [isContentPanelExpanded, setContentPanelExpanded] = useState(true);
  const [error, setError] = useState<ErrorLike | null>(null);
  const scrollToPageRef = useRef<(pageId: T) => void>(() => {});
  const iFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [activeIframeRef, setActiveIframeRef] =
    useState<MutableRefObject<HTMLIFrameElement | null>>(iFrameRef);
  const toggleStepVisibilityRef = useRef<((index: number) => void) | null>(
    null,
  );
  const [mobileTab, setMobileTab] = useState<'viewer' | 'editor'>('editor');
  const [renderFilename, setRenderFilename] = useState<string | undefined>(
    undefined,
  );
  const [descriptionLanguage, setDescriptionLanguage] =
    useState<Language | null>(null);
  const [markup, setMarkup] = useState<string | null>(null);

  const { posthog } = useAnalytics();
  useEffect(() => {
    posthog.startSessionRecording();
  }, [posthog]);

  const stepsWithExit = useMemo(() => {
    const exitStep: StepType<T> = {
      title: t('Save and exit'),
      icon: <LogOutIcon size={20} />,
      onClick: () =>
        navigate({
          pathname: exitPath,
          search: searchParams.toString(),
        }),
      position: 'footer',
    };
    return [...steps, exitStep];
  }, [t, steps, navigate, exitPath, searchParams]);

  // Filter steps by position (main or footer)
  const { mainSteps, footerSteps } = useMemo(() => {
    const mainSteps = stepsWithExit.filter(step => step.position === 'main');
    const footerSteps = stepsWithExit.filter(
      step => step.position === 'footer',
    );
    return { mainSteps, footerSteps };
  }, [stepsWithExit]);

  const navigableSteps = useMemo(() => {
    return stepsWithExit.filter(step => {
      if (typeof step.navigable === 'boolean') {
        return step.navigable;
      }

      return step.position === 'main';
    });
  }, [stepsWithExit]);

  const componentSteps = useMemo(
    () =>
      stepsWithExit.filter(step => 'pageId' in step && Boolean(step.pageId)),
    [stepsWithExit],
  );

  const defaultPageId = useMemo(() => {
    const foundStep = componentSteps.find(step => step.pageId === pageId);
    return foundStep?.pageId ?? (componentSteps[0]?.pageId as T);
  }, [componentSteps, pageId]);

  const [viewerPageId, setViewerPageId] = useState<T>(defaultPageId);

  const currentStepData = useMemo(
    () =>
      stepsWithExit.find(
        step => 'pageId' in step && step.pageId === viewerPageId,
      ) || stepsWithExit[0],
    [stepsWithExit, viewerPageId],
  );

  const currentStepIndex = useMemo(
    () =>
      stepsWithExit.findIndex(
        step => 'pageId' in step && step.pageId === viewerPageId,
      ),
    [stepsWithExit, viewerPageId],
  );

  const setCurrentStepByIndex = useCallback(
    (index: number) => {
      const targetPageId = steps[index].pageId;
      if (!targetPageId) {
        return;
      }

      // Scroll even when re-clicking the already active step
      scrollToPageRef.current(targetPageId);
      setViewerPageId(targetPageId);
      navigate(
        {
          pathname: `./../${targetPageId}`,
          search: searchParams.toString(),
        },
        { replace: true },
      );
    },
    [steps, navigate, searchParams],
  );

  const handleScrollToPage = useCallback(
    (pageId: T, index: number) => {
      scrollToPageRef.current(pageId);
      setCurrentStepByIndex(index);
    },
    [setCurrentStepByIndex],
  );

  const contextValue = useMemo(
    () => ({
      mobileTab,
      setMobileTab,
      setCurrentStepByIndex,
      documentId,
      templateId,
      viewerPageId,
      setViewerPageId,
      steps: stepsWithExit,
      mainSteps,
      footerSteps,
      navigableSteps,
      data,
      currentStepData,
      currentStepIndex,
      setError,
      isSidebarExpanded,
      setSidebarExpanded,
      isContentPanelExpanded,
      setContentPanelExpanded,
      documentType,
      handleScrollToPage,
      scrollToPageRef,
      iFrameRef,
      activeIframeRef,
      setActiveIframeRef,
      language: language ?? defaultLanguage,
      currentStep: currentStepData as StepType<T>,
      includeFields,
      renderFilename,
      setRenderFilename,
      toggleStepVisibilityRef,
      previewOnly,
      readOnly,
      previewVersion,
      descriptionLanguage,
      setDescriptionLanguage,
      markup,
      setMarkup,
    }),
    [
      mobileTab,
      setCurrentStepByIndex,
      documentId,
      templateId,
      viewerPageId,
      stepsWithExit,
      mainSteps,
      footerSteps,
      navigableSteps,
      data,
      currentStepData,
      currentStepIndex,
      isSidebarExpanded,
      isContentPanelExpanded,
      documentType,
      handleScrollToPage,
      activeIframeRef,
      language,
      defaultLanguage,
      includeFields,
      renderFilename,
      previewOnly,
      readOnly,
      previewVersion,
      descriptionLanguage,
      markup,
    ],
  );

  return (
    <ReportEditorContext.Provider
      value={contextValue as ReportEditorContextType<any>}
    >
      {children}
      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </ReportEditorContext.Provider>
  );
};

export const useReportContext = <T extends PageId>() => {
  const context = useContext(ReportEditorContext);
  if (!context) {
    throw new Error(
      'useReportContext must be used within a ReportEditorProvider',
    );
  }

  return context as ReportEditorContextType<T>;
};

export default ReportEditorProvider;
